import { FC, useEffect, useState } from 'react';
import { Loader, MultiSelect, Select, Text, TextInput } from '@mantine/core';
import { IconAt, IconMailForward, IconPaperclip, IconTemplate } from '@tabler/icons';
import { i18n } from '@lingui/core';
import { SendMailConfigDTO, WorkflowConfigProps } from '../../../../../Types/LogT';
import { DataS } from '../../../../../Service/DataS';
import { MessageS } from '../../../../../Service/MessageS';
import { MessageTemplateDto } from '../../../../../Types/MessageT';
import { WorkflowS } from '../../../../../Service/WorkflowS';
import AutocompleteResourceTypeItem from '../../../../../Organismns/Resources/table/AutocompleteResourceTypeItem';
import { ThemeText } from '../../../../../Atoms/ThemeText';

const SendMailConfig: FC<WorkflowConfigProps> = ({ config, handleChange, availableEntities }) => {
  const { templateId, attachments, receiver, recipient, cc, bcc } = config as SendMailConfigDTO;
  const [templates, setTemplates] = useState<MessageTemplateDto[]>([]);
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const availableAttachments = WorkflowS.filterEntities(availableEntities, [
    'documents',
    'questionnaires',
    'contracts',
  ]);
  const initialAttachments = attachments ? attachments.map((a) => a.id) : [];

  useEffect(() => {
    setLoading(true);
    MessageS.fetchTemplates()
      .then(setTemplates)
      .finally(() => setLoading(false));
  }, []);

  const emailOptions = [...(cc ?? []), ...(bcc ?? []), ...additionalEmails].filter(
    (email, index, emails) =>
      emails.findIndex((currentEmail) => currentEmail.toLowerCase() === email.toLowerCase()) === index,
  );

  const createEmailOption = (query: string) => {
    const email = query.trim();
    setAdditionalEmails((currentEmails) =>
      currentEmails.some((currentEmail) => currentEmail.toLowerCase() === email.toLowerCase())
        ? currentEmails
        : [...currentEmails, email],
    );
    return email;
  };

  const getCreateEmailLabel = (query: string) => (
    <Text color="blue" size="sm">
      + E-Mail-Adresse hinzufügen
      <ThemeText span ml="xs" weight="bolder">
        {query}
      </ThemeText>
    </Text>
  );

  return (
    <>
      <Select
        required
        label="Empfänger"
        icon={<IconMailForward size={20} />}
        data={DataS.emailReceiverOptions}
        value={receiver}
        itemComponent={AutocompleteResourceTypeItem}
        onChange={(val) => handleChange({ receiver: val })}
      />
      {receiver === 'CUSTOM_ADDRESS' && (
        <TextInput
          required
          label="An"
          mt="sm"
          icon={<IconAt size={20} />}
          defaultValue={recipient ?? undefined}
          onBlur={(e) => handleChange({ recipient: e.currentTarget.value })}
        />
      )}
      <MultiSelect
        mt="sm"
        creatable
        searchable
        clearable
        value={cc ?? []}
        onChange={(emails) => handleChange({ cc: emails })}
        onCreate={createEmailOption}
        getCreateLabel={getCreateEmailLabel}
        label="Cc"
        placeholder="E-Mail-Adresse eingeben"
        data={emailOptions}
      />
      <MultiSelect
        mt="sm"
        creatable
        searchable
        clearable
        value={bcc ?? []}
        onChange={(emails) => handleChange({ bcc: emails })}
        onCreate={createEmailOption}
        getCreateLabel={getCreateEmailLabel}
        label="Bcc"
        placeholder="E-Mail-Adresse eingeben"
        data={emailOptions}
      />
      <Select
        required
        mt="sm"
        icon={<IconTemplate size={20} />}
        label="Mail-Vorlage"
        rightSection={loading && <Loader size="xs" />}
        disabled={loading}
        data={DataS.getMessageTemplateOptions(templates, i18n)}
        onChange={(id) => handleChange({ templateId: id })}
        value={templateId ? templateId.toString() : null}
      />
      <MultiSelect
        mt="sm"
        defaultValue={initialAttachments}
        onChange={(entities) => handleChange({ attachments: entities })}
        icon={<IconPaperclip size={20} />}
        label="Anhang"
        data={DataS.getEntityReferencesOptions(availableAttachments)}
      />
      <Text mt={5} mb="sm" size="xs" color="dimmed">
        Hänge zuvor erstellte Dokumente an diese E-Mail an.
      </Text>
    </>
  );
};

export default SendMailConfig;
